import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import images from "../../images";
import Copyright from "../../layout/Copyright/Copyright";
import { useSignupMutation } from "../../services/apiSlice";
import { FormFieldSigIn } from "../../components/common/Input/InputSignIn";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export default function SignUpPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [hidePassword, setHidePassword] = useState({
    password: true,
    confirmPassword: true,
  });
  const [register, { isLoading }] = useSignupMutation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSignUp = async () => {
    if (isLoading) return;

    if (formData.password !== formData.confirmPassword) {
      toast.error(t("signUp.passwordMismatch"));
      return;
    }

    if (!formData.email || !formData.email.includes("@")) {
      toast.error(t("signUp.pleseEnterValidEmail"));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t("signUp.passwordInvalid"));
      return;
    }

    try {
      const body = {
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
      };

      const response = await register(body).unwrap();

      if (
        response.message === "Sign up successfully, please verify your email" ||
        response.status === 200
      ) {
        toast.success(t("signUp.signUpSuccess"));
        navigate(
          `/verify-otp?business_id=${encodeURIComponent(response.data)}`
        );
      }
    } catch (err) {
      toast.error(err?.data?.detail[0].msg || t("signUp.signUpFailed"));
    }
  };

  const togglePasswordVisibility = (field) => {
    setHidePassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <main className="mx-auto w-full bg-yellow-50 max-w-[480px]">
      <Helmet>
        <meta name="theme-color" content="#fefce8" />
      </Helmet>
      <section className="flex justify-center items-center w-full h-[300px]">
        <img src={images.LogoLogin} className="w-[180px]" alt="Logo" />
      </section>
      <section className="flex flex-col justify-between px-4 py-6 w-full bg-white min-h-[calc(100dvh-300px)] rounded-t-[20px] shadow-[0px_0px_4px_rgba(0,0,0,0.08)]">
        <div className="flex flex-col items-center justify-center w-full max-w-[340px] mx-auto">
          <section className="w-full text-center">
            <div className="flex flex-col items-center">
              <h1 className="text-4xl font-bold text-gray-800">
                {t("signUp.title")}
              </h1>
              <div className="mt-3">
                <span className="inline-block px-3 py-1.5 text-xs text-amber-400 bg-yellow-50 rounded-xl">
                  {t("signUp.businessAccount")}
                </span>
              </div>
            </div>
          </section>

          <div className="flex flex-col w-full text-sm text-neutral-500">
            <FormFieldSigIn
              placeholder={t("signUp.email")}
              icon={images.LetterIcon}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
            />

            <FormFieldSigIn
              placeholder={t("signUp.password")}
              icon={images.KeyIcon}
              type={hidePassword.password ? "password" : "text"}
              handleRightIcon={() => togglePasswordVisibility("password")}
              rightIcon={images.EyesIcon}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
            />

            <FormFieldSigIn
              placeholder={t("signUp.confirmPassword")}
              icon={images.KeyIcon}
              type={hidePassword.confirmPassword ? "password" : "text"}
              handleRightIcon={() =>
                togglePasswordVisibility("confirmPassword")
              }
              rightIcon={images.EyesIcon}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              disabled={isLoading}
            />

            <button
              onClick={handleSignUp}
              className={`w-full px-4 py-3 mt-5 text-lg font-semibold text-white bg-[#3F54DF] rounded-xl ${
                isLoading
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:bg-indigo-700"
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("base.processing")}
                </div>
              ) : (
                t("signUp.title")
              )}
            </button>

            <p className="mt-5 w-full text-center text-sm">
              <span
                style={{
                  fontFamily:
                    "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: 400,
                  color: "rgba(120,122,122,1)",
                }}
              >
                {t("signUp.alreadyHaveAccount")}
              </span>
              <span
                onClick={() => !isLoading && navigate("/sign-in")}
                style={{
                  fontFamily:
                    "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: 700,
                  color: "rgba(63,84,223,1)",
                  cursor: isLoading ? "default" : "pointer",
                  opacity: isLoading ? 0.7 : 1,
                }}
              >
                {t("signUp.signIn")}
              </span>
            </p>
          </div>
        </div>
        <div className="flex justify-center">
          <Copyright />
        </div>
      </section>
    </main>
  );
}
